import { cn } from "cn";
import { Loader2, MessageSquareText } from "lucide-react";
import * as React from "react";
import { Button } from "./button";
import { Textarea } from "./input";
import { Dialog } from "./sheet";

// Early access (a feature an account sees before everyone else): a small
// brand-tinted "Beta" pill next to the feature's title, and "Send feedback
// on this beta", a quiet button that opens a sheet with one text field
// (bottom sheet on phones, centred card from 640px). Copy comes from the
// caller; the caller sends the text.

export function BetaBadge({
  label = "Beta",
  title,
  className,
  testId,
}: {
  label?: string;
  /** Tooltip, e.g. "You have early access to this feature". */
  title?: string;
  className?: string;
  testId?: string;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex h-5 shrink-0 select-none items-center rounded-full bg-v3-brand-tint px-2 align-middle font-v3 text-[11px] font-semibold uppercase leading-none tracking-[0.06em] text-v3-brand-strong",
        className,
      )}
      data-slot="beta-badge"
      data-testid={testId}
      title={title}
    >
      {label}
    </span>
  );
}

export interface BetaFeedbackLabels {
  /** The button: "Send feedback on this beta". */
  trigger: string;
  /** The sheet's title. */
  title: string;
  /** Under the title: who reads it, what to write. */
  description?: string;
  /** The field's accessible name. */
  field: string;
  placeholder?: string;
  submit: string;
  cancel: string;
  close: string;
  /** Shown when sending fails and `describeError` gives nothing better. */
  failed: string;
}

export interface BetaFeedbackProps {
  labels: BetaFeedbackLabels;
  /** Sends the trimmed text; resolve to close the sheet, reject to keep it. */
  onSubmit: (text: string) => Promise<void>;
  /** The message shown when `onSubmit` rejects (default `labels.failed`). */
  describeError?: (error: unknown) => string | undefined;
  maxLength?: number;
  /** Starts open (stories, tests). */
  defaultOpen?: boolean;
  /** "button": a quiet pill; "link": inline text, for tight spaces. */
  appearance?: "button" | "link";
  className?: string;
  testId?: string;
}

export function BetaFeedback({
  labels,
  onSubmit,
  describeError,
  maxLength = 4000,
  defaultOpen = false,
  appearance = "button",
  className,
  testId = "beta-feedback",
}: BetaFeedbackProps): React.JSX.Element {
  const [open, setOpen] = React.useState(defaultOpen);
  const [text, setText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const fieldId = React.useId();
  const field = React.useRef<HTMLTextAreaElement>(null);
  const errorId = React.useId();
  const message = text.trim();

  const change = (next: boolean): void => {
    if (busy) return;
    setOpen(next);
    if (!next) {
      setText("");
      setError(null);
    }
  };
  const submit = async (): Promise<void> => {
    if (!message || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(message);
      setBusy(false);
      setOpen(false);
      setText("");
    } catch (failure) {
      setBusy(false);
      setError(describeError?.(failure) ?? labels.failed);
    }
  };

  return (
    <>
      <Button
        className={cn(
          appearance === "link"
            ? "h-auto min-h-11 px-1 font-medium text-v3-text-2 underline-offset-4 hover:bg-transparent hover:underline active:scale-100 lg:h-auto lg:min-h-8 lg:px-1"
            : "max-lg:h-11",
          className,
        )}
        data-testid={testId}
        variant="ghost"
        onClick={() => change(true)}
      >
        <MessageSquareText aria-hidden />
        {labels.trigger}
      </Button>
      <Dialog
        actions={
          <>
            <Button
              className="max-sm:h-12"
              disabled={busy}
              onClick={() => change(false)}
            >
              {labels.cancel}
            </Button>
            <Button
              className="max-sm:h-12"
              data-testid={`${testId}-submit`}
              disabled={!message || busy}
              form={`${fieldId}-form`}
              type="submit"
              variant="primary"
            >
              {busy && <Loader2 aria-hidden className="animate-spin" />}
              {labels.submit}
            </Button>
          </>
        }
        closeLabel={labels.close}
        description={labels.description}
        open={open}
        testId={`${testId}-sheet`}
        title={labels.title}
        onOpenAutoFocus={(event) => {
          // Straight to the field.
          event.preventDefault();
          field.current?.focus();
        }}
        onOpenChange={change}
      >
        <form
          className="mt-2"
          id={`${fieldId}-form`}
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <Textarea
            aria-describedby={error ? errorId : undefined}
            aria-invalid={error ? true : undefined}
            aria-label={labels.field}
            className="min-h-32"
            data-testid={`${testId}-field`}
            disabled={busy}
            id={fieldId}
            maxLength={maxLength}
            placeholder={labels.placeholder}
            ref={field}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                void submit();
              }
            }}
          />
          {error && (
            <p
              className="mt-2 text-[13px] leading-snug text-v3-rec-text"
              id={errorId}
              role="alert"
            >
              {error}
            </p>
          )}
        </form>
      </Dialog>
    </>
  );
}
